import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const badges = [
  { icon: "ri-award-line",        label: "Same-Day Certification" },
  { icon: "ri-heart-pulse-line",  label: "AHA-Aligned" },
  { icon: "ri-hand-heart-line",   label: "Hands-On Training" },
  { icon: "ri-flashlight-line",   label: "Fast Booking" },
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image — diverse CPR classroom, students on mannequins, no instructor */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 w-full h-full"
          style={{ animation: "heroZoom 18s ease-in-out infinite alternate" }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/6314cfb4d58c8dc96495a8474434363f.jpg"
            alt="Diverse community members actively performing CPR compressions in Kissimmee training classroom"
            className="w-full h-full object-cover object-top"
            style={{ transformOrigin: "center center" }}
          />
        </div>

        {/* Black / deep-purple gradient blend for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-[#1a0535]/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/25" />
        <div className="absolute inset-0" style={{ background: "rgba(30,6,60,0.22)" }} />
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes heroZoom {
          0%   { transform: scale(1);    }
          100% { transform: scale(1.08); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes fadeSlideLeft {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0);     }
        }
        @keyframes badgeFade {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-eyebrow  { animation: fadeSlideLeft 0.7s ease forwards; animation-delay: 0.1s;  opacity: 0; }
        .hero-headline { animation: fadeSlideUp  0.8s ease forwards; animation-delay: 0.35s; opacity: 0; }
        .hero-sub      { animation: fadeSlideUp  0.8s ease forwards; animation-delay: 0.6s;  opacity: 0; }
        .hero-ctas     { animation: fadeSlideUp  0.8s ease forwards; animation-delay: 0.85s; opacity: 0; }
        .hero-badge-0  { animation: badgeFade    0.6s ease forwards; animation-delay: 1.05s; opacity: 0; }
        .hero-badge-1  { animation: badgeFade    0.6s ease forwards; animation-delay: 1.18s; opacity: 0; }
        .hero-badge-2  { animation: badgeFade    0.6s ease forwards; animation-delay: 1.31s; opacity: 0; }
        .hero-badge-3  { animation: badgeFade    0.6s ease forwards; animation-delay: 1.44s; opacity: 0; }
      `}</style>

      {/* Content */}
      <div className={`relative z-10 w-full mx-auto px-6 lg:px-16 pt-28 pb-20 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}>
        <div className="max-w-3xl">

          {/* Eyebrow pill */}
          <div className="hero-eyebrow inline-flex items-center gap-2 bg-[#c9a227]/20 border border-[#c9a227]/50 text-[#c9a227] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
            <i className="ri-verified-badge-line text-sm" />
            Kissimmee&apos;s Premier CPR Training Center
          </div>

          {/* H1 */}
          <h1 className="hero-headline font-serif text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
            Top-Tier CPR &amp; Life Support{" "}
            <br className="hidden lg:block" />
            Training in{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #c9a227, #f0d060, #c9a227)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Kissimmee
            </span>
          </h1>

          {/* Subheadline */}
          <p className="hero-sub text-lg lg:text-xl text-white/85 leading-relaxed mb-8 max-w-2xl">
            <strong className="text-[#c9a227] font-semibold">American Heart Association–Aligned</strong>{" "}
            Training with Same-Day Certification. Real hands-on practice that prepares you to save a life — starting today.
          </p>

          {/* CTAs */}
          <div className="hero-ctas flex flex-wrap gap-4 mb-12">
            <Link
              to="/booking#reserve-spot"
              className="inline-flex items-center gap-2 bg-[#c9a227] text-black font-bold text-base px-9 py-4 rounded-md hover:bg-[#f0d060] transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ boxShadow: "0 4px 28px rgba(201,162,39,0.45)" }}
            >
              <i className="ri-calendar-check-line text-lg" />
              Book Now
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 border-2 border-white/70 text-white font-semibold text-base px-9 py-4 rounded-md hover:bg-white hover:text-[#2d0a4e] transition-all duration-200 cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-book-open-line text-lg" />
              View Courses
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-3">
            {badges.map((badge, i) => (
              <div
                key={badge.label}
                className={`hero-badge-${i} flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-white/20 hover:border-[#c9a227]/50 transition-all duration-200 cursor-default`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${badge.icon} text-[#c9a227]`} />
                </div>
                {badge.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 text-xs z-10">
        <span className="tracking-wider text-xs uppercase">Scroll to explore</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line animate-bounce text-base" />
        </div>
      </div>
    </section>
  );
}
